import {
  AbsoluteFill,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
} from 'remotion';

// Utils
import { combineClasses, createAnimationStyles } from '@/utils';
import { processImageUrlForRemotion } from '@/utils/url';

// Constants
import {
  CLAMP_RIGHT,
  DEFAULT_ANIMATION_STATE,
  EMPLOYEE_YEAR_IN_REVIEW_CONFIG,
} from '@/constants';
import { IMAGES } from '@shared/constants';
import { TEXT_ANIMATIONS_CONFIG, LOGO_ANIMATION_CONFIG } from './constants';

// Types
import { EmployeeYearInReview } from '@/types';

// Hooks
import { useTextAnimations } from '@/hooks';

// Components
import { FallbackRemotionImg } from '@/components/common/FallbackRemotionImg';

// Mocks
import { mockEmployeeYearInReview } from '@/mocks';

// Local components
import { DecorativeYear } from './components/DecorativeYear';
import { DecorativeCircle } from './components/DecorativeCircle';
import { AnimatedBadge } from './components/AnimatedBadge';
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Utils
import { parseOpeningSentence } from './utils';

// Common
import { AnimatedText } from '@/components/common/AnimatedText';

/**
 * Scene 1: Branded intro for the Employee Year in Review.
 *
 * Shows background decorative elements (large year + circle), the Agility
 * logo, a badge reading "a year of", a 3-line "impact and achievement"
 * headline, and the featured year value.
 */
export const Scene1 = ({
  year = mockEmployeeYearInReview.year,
  openingSentence = mockEmployeeYearInReview.openingSentence,
}: EmployeeYearInReview) => {
  const { colors, typography } = employeeYearInReviewTheme;
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const {
    timing: { slide1 },
  } = EMPLOYEE_YEAR_IN_REVIEW_CONFIG;

  const defaultOpeningSentence = mockEmployeeYearInReview.openingSentence;
  const { highlightText, impactWords } = parseOpeningSentence(
    openingSentence,
    defaultOpeningSentence,
  );

  // Custom logo animation with 1 second longer duration and slower spring
  const logoDelay = TEXT_ANIMATIONS_CONFIG.START_FRAME;
  const logoDuration = LOGO_ANIMATION_CONFIG.DURATION_IN_FRAMES;

  const logoScale = spring({
    frame: frame - logoDelay,
    fps,
    config: LOGO_ANIMATION_CONFIG.SPRING_CONFIG,
  });

  const logoOpacity = interpolate(
    frame,
    [logoDelay, logoDelay + logoDuration],
    [0, 1],
    CLAMP_RIGHT,
  );

  const logoAnim = { scale: logoScale, opacity: logoOpacity };

  // Year animation uses standard timing
  const [, yearAnim = DEFAULT_ANIMATION_STATE] = useTextAnimations(2, {
    stagger: TEXT_ANIMATIONS_CONFIG.STAGGER,
    springConfig: TEXT_ANIMATIONS_CONFIG.SPRING_CONFIG,
    startFrame: TEXT_ANIMATIONS_CONFIG.START_FRAME,
  });

  const yearString = year.toString();
  const logoSrc = processImageUrlForRemotion(
    IMAGES.LOGO_AGILITY_WHITE,
    staticFile,
  );

  const badgeStartFrame =
    TEXT_ANIMATIONS_CONFIG.START_FRAME + TEXT_ANIMATIONS_CONFIG.STAGGER;
  const impactStartFrame =
    TEXT_ANIMATIONS_CONFIG.START_FRAME + TEXT_ANIMATIONS_CONFIG.STAGGER * 2;
  const impactTextClass = `${colors.textPrimary} ${typography.base}`;

  let impactStart = impactStartFrame;
  let previousWordLength = 0;
  const impactWordsWithStart = impactWords.map((word, index) => {
    if (index > 0) {
      impactStart += previousWordLength * 2;
    }

    previousWordLength = word.length;
    return { word, startFrame: impactStart };
  });

  return (
    <Sequence
      name="Root"
      from={slide1.start}
      durationInFrames={slide1.duration}
    >
      <AbsoluteFill className={`relative overflow-hidden ${colors.bgPrimary}`}>
        <DecorativeYear year={year} />

        <DecorativeCircle />

        {/* Main Content */}
        <div className="flex flex-col pl-[481px] pt-[146px] h-full z-10 relative">
          {/* Agility Logo - Professional slide from left */}
          {logoSrc && (
            <div style={createAnimationStyles.slideFromLeft(logoAnim)}>
              <FallbackRemotionImg
                src={logoSrc}
                alt="Agility Logo"
                className="h-13.5 w-auto"
              />
            </div>
          )}

          {/* Content Container */}
          <div className="flex flex-col items-start mt-16 pl-8">
            {/* Badge - Box expansion with character reveal */}
            <AnimatedBadge
              text={highlightText}
              className={`${colors.badgeBackground.primary} ${colors.badgeText} ${typography.badge} px-5 py-1.5`}
              startFrame={badgeStartFrame}
              springPreset={TEXT_ANIMATIONS_CONFIG.SPRING_CONFIG}
            />

            {/* Impact words - Character-by-character reveal for dramatic effect */}
            <div className="flex flex-col -space-y-4">
              {impactWordsWithStart.map(({ word, startFrame }, index) => (
                <AnimatedText
                  key={`${word}-${index}`}
                  text={word}
                  className={combineClasses(
                    impactTextClass,
                    index === 1 && 'text-primary-300',
                  )}
                  startFrame={startFrame}
                  staggerFrames={2}
                  springPreset={TEXT_ANIMATIONS_CONFIG.SPRING_CONFIG}
                />
              ))}
            </div>

            {/* Year - Final bouncy entrance */}
            <div className="mt-1" style={createAnimationStyles.popIn(yearAnim)}>
              <span className={`${colors.textPrimary} ${typography.year}`}>
                {yearString}
              </span>
            </div>
          </div>
        </div>
      </AbsoluteFill>
    </Sequence>
  );
};
